/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable max-len -- verbatim prototype strings and golden rows exceed the line limit */
// pcg-core: timing statistics, consistency classification, and rate/rhythm
// patterns (core-viewer.js 224-258, 2908-3289).
//
// Deviations (see MODULE-MAP.md):
// - CANONICAL exam-viewer mode hard-enabled in timingStats: the APP_VERSION
//   allow-list is deleted; the S/D ratio is the canonical
//   ratio_of_mean_accepted_cycle_durations (6 decimal places), with the legacy
//   mean-of-per-cycle-ratios retained only as the `legacyMeanCycleRatio` audit field.
// - For a guide with a timing-engine Evaluation Range, timingStats reports the
//   engine's median-based range values instead (see timingStats), and
//   evaluationRangeProvenance describes where those values came from.
// - The exam-overlay globals window.__medaicaExamTimingConsistency /
//   window.__medaicaCurrentRecordingId become explicit context fields
//   (ctx.examTimingConsistency / ctx.currentRecordingId).

import { mean, median, sd, percentile, normalizedSuccessiveDifferencePct, lagAutocorrelation } from "./stats"
import { clinicalPeaks, cycleAuditIds, canonicalTimingDurationSummary, lowConfidenceMarker } from "./cycles"
import { rejectGateMetrics } from "./gate"
import { analysisQuality } from "./analysis-quality"
import { fmtNumber } from "./format"
import { MIN_USABLE_CYCLES } from "./constants"
import { ENGINE_NONE } from "./evaluation-range"

export function statusFromTiming(timing) {
  if (!timing || timing.label === "Limited") return "amber"
  if (timing.label === "Expected PCG timing variability") return "green"
  if (timing.label === "Moderate PCG timing variability") return "amber"
  if (timing.label === "High PCG timing variability") return "red"
  if (timing.label === "Periodic PCG timing pattern") return "amber"
  if (timing.label === "Expected PCG timing") return "green"
  if (timing.label === "Normal" || timing.label === "Expected timing") return "green"
  if (timing.label === "Increased PCG timing variability") return "amber"
  if (timing.label === "Borderline irregular") return "amber"
  if (timing.label === "Borderline timing variability") return "amber"
  if (timing.label === "Periodic timing variation") return "amber"
  if (timing.label === "Markedly increased PCG timing variability") return "red"
  if (timing.label === "Irregular") return "red"
  if (timing.label === "Elevated timing variability") return "red"
  if (timing.index >= 85) return "green"
  if (timing.index >= 65) return "amber"
  return "red"
}

export function timingDisplayLabel(label) {
  if (label === "Expected PCG timing variability") return "Normal"
  if (label === "Moderate PCG timing variability") return "Moderate"
  if (label === "High PCG timing variability") return "Elevated"
  if (label === "Periodic PCG timing pattern") return "Periodic"
  if (label === "Limited") return "Unassessable"
  return label
}

export function timingInternalLabel(label) {
  if (label === "Normal") return "Expected PCG timing variability"
  if (label === "Moderate") return "Moderate PCG timing variability"
  if (label === "Elevated") return "High PCG timing variability"
  if (label === "Periodic") return "Periodic PCG timing pattern"
  // "N/A" is the pre-v0.8.3 display label for the limited state; both map back.
  if (label === "N/A" || label === "Unassessable") return "Limited"
  return label
}

export function confidenceRationaleText(codes) {
  const labels = {
    low_cycle_count: "shorter usable cycle set",
    inferred_markers_high: "higher inferred-marker share",
    low_confidence_marker_share: "lower high-confidence marker share",
    guide_quality_low: "low S1/S2 guide quality",
    guide_quality_moderate: "moderate S1/S2 guide quality",
    orientation_score_weak: "weak S1/S2 orientation score",
    noise_floor_flag: "signal noise-floor concern",
    periodic_assessment_limited: "periodic assessment near its minimum cycle count",
  }
  const unique = [...new Set((codes || []).filter(Boolean))]
  if (!unique.length) return ""
  return unique.map((c) => labels[c] || c).join("; ")
}

export function ratePatternFromHr(hr) {
  const n = Number(hr)
  if (!Number.isFinite(n))
    return {
      label: "N/A",
      className: "unavailable",
      hover: "Rate range unavailable because there are not enough usable PCG S1-to-S1 cycles.",
    }
  if (n < 60)
    return {
      label: "Low",
      className: "",
      hover:
        "Bradycardic range by adult resting heart-rate convention (<60 bpm). Derived from PCG S1-to-S1 cycle timing, not ECG RR intervals.",
    }
  if (n > 100)
    return {
      label: "Elevated",
      className: "",
      hover:
        "Tachycardic range by adult resting heart-rate convention (>100 bpm). Derived from PCG S1-to-S1 cycle timing, not ECG RR intervals.",
    }
  return {
    label: "Normal",
    className: "",
    hover:
      "Expected adult resting heart-rate range by convention (60-100 bpm). Derived from PCG S1-to-S1 cycle timing, not ECG RR intervals.",
  }
}

export function alternatingCyclePattern(cycles, cycleMed) {
  if (!cycles || cycles.length < 6 || !Number.isFinite(cycleMed) || cycleMed <= 0) return false
  const deviations = cycles.map((c) => (c.cycle - cycleMed) / cycleMed)
  const marked = deviations.map((d) => (Math.abs(d) >= 0.04 ? (d > 0 ? 1 : -1) : 0))
  const nonZero = marked.filter((v) => v !== 0)
  if (nonZero.length < Math.max(6, Math.floor(marked.length * 0.65))) return false
  let alternating = 0,
    comparable = 0
  for (let i = 1; i < marked.length; i++) {
    if (marked[i] === 0 || marked[i - 1] === 0) continue
    comparable += 1
    if (marked[i] !== marked[i - 1]) alternating += 1
  }
  if (comparable < 5 || alternating / comparable < 0.75) return false
  const shortVals = deviations.filter((d) => d <= -0.04).map(Math.abs)
  const longVals = deviations.filter((d) => d >= 0.04)
  const shortCv = shortVals.length > 1 ? sd(shortVals) / (mean(shortVals) || 1) : 0
  const longCv = longVals.length > 1 ? sd(longVals) / (mean(longVals) || 1) : 0
  return shortCv <= 0.55 && longCv <= 0.55
}

export function bpmAdjustedTimingThresholds(hr) {
  const normalSwing = Number.isFinite(Number(hr)) && Number(hr) < 60 ? 11 : 8
  return {
    normalSwingPct: normalSwing,
    moderateMaxSwingPct: 18,
    elevatedSwingPct: 18,
    referenceHighSwingPct: 25,
    normalMaxPct: normalSwing * 2,
    irregularMinPct: 36,
    referenceHighPct: 50,
    rateAdjusted: Number.isFinite(Number(hr)) && Number(hr) < 60,
  }
}

export const ENGINE_RATIO_CALCULATION = "ratio_of_engine_median_durations"
/** How the timing engine's heart rate is calculated. */
export const ENGINE_HEART_RATE_METHOD = "60000 / median S1-to-S1 interval"
/** How the cycle calculation's heart rate is calculated (a guide without the engine's range values). */
export const CYCLE_HEART_RATE_METHOD = "60 / mean cycle length over the selected usable cycles"
export const EVALUATION_RANGE_PROVENANCE_SCHEMA = "medaica.pcg.evaluation_range_provenance.v1"
/** What the timing engine's S1-to-S1 interval CV is, wherever it is surfaced. */
export const S1_INTERVAL_CV_DESCRIPTION =
  "S1-to-S1 interval CV: the coefficient of variation of the timing engine's S1-to-S1 intervals over the Evaluation Range. An internal stability / confidence measure (it grades the Confidence row's Timing and Variation); the reported timing variability is the viewer's central spread."

const finiteNumber = (value) => typeof value === "number" && Number.isFinite(value)
const numberOrNull = (value) => (finiteNumber(value) ? value : null)

// The timing engine's results for its Evaluation Range, or null when the range
// lacks any of them: heart rate (60000 / median S1-to-S1 interval), median
// cycle, systole and diastole durations (ms).
function engineRangeTiming(evaluationRange) {
  const timing = evaluationRange?.timing
  if (!timing) return null
  const { bpm, rrMedianMs, systoleMedianMs, diastoleMedianMs } = timing
  if (![bpm, rrMedianMs, systoleMedianMs, diastoleMedianMs].every(finiteNumber)) return null
  if (rrMedianMs <= 0 || diastoleMedianMs <= 0) return null
  return { bpm, rrMs: rrMedianMs, sysMs: systoleMedianMs, diaMs: diastoleMedianMs }
}

// The timing engine's S1-to-S1 interval CV over its Evaluation Range (its
// `rrCv`), or null without one
const s1IntervalCvOf = (evaluationRange) => {
  const rrCv = evaluationRange?.timing?.rrCv
  return finiteNumber(rrCv) ? rrCv : null
}

// For a guide whose timing engine selected an Evaluation Range, the engine's
// median-based range values are the timing results: heart rate, cycle, SYS,
// DIA and the SYS:DIA ratio come from it, so the viewer, its export package
// and the Report all carry the values the engine stored. The cycles still
// drive Energy, Structural Sound, the audit cycle ids and the timing
// variability (timingConsistency's central spread, from the engine's range
// markers); `cv` stays the cycles' coefficient of variation. The engine's own
// S1-to-S1 interval CV is kept apart as `s1IntervalCv` (null without an
// Evaluation Range): an internal stability / confidence measure, never the
// reported timing variability. The cross-position half/double correction does
// not apply to these guides. Guides without the engine keep the cycle-mean
// calculation.
export function timingStats(ctx) {
  const stats = { ...cycleTimingStats(ctx), s1IntervalCv: s1IntervalCvOf(ctx.evaluationRange) }
  const engine = engineRangeTiming(ctx.evaluationRange)
  if (!engine) return stats
  const sdRatio = engine.sysMs / engine.diaMs
  const hr = Math.round(engine.bpm)
  return {
    ...stats,
    hr,
    rawHr: hr,
    evalRangeBpm: engine.bpm,
    examConsensusCorrection: null,
    cycle: engine.rrMs.toFixed(0),
    sys: engine.sysMs.toFixed(0),
    dia: engine.diaMs.toFixed(0),
    sysExactMs: engine.sysMs,
    diaExactMs: engine.diaMs,
    ratio: sdRatio.toFixed(6),
    ratioCalculation: ENGINE_RATIO_CALCULATION,
    canonicalSdRatio: sdRatio,
    frac: String(Math.round((engine.sysMs / engine.rrMs) * 100)),
  }
}

/**
 * Where a timing-engine guide's timing results come from, for the export
 * package and the audit: null for a guide without the engine. With the engine
 * ready, the selected Evaluation Range (bounds, cycle count, the engine's
 * outcome and selection mode), the engine package that produced it, how the
 * heart rate and SYS:DIA were calculated, the engine's S1-to-S1 interval CV
 * (described as such) and the method of the reported timing variability.
 * With the engine present but not ready (status "unavailable": no timing
 * results are reported), the range and calculation fields are null.
 */
export function evaluationRangeProvenance(ctx) {
  const engineState = ctx.engine
  if (!engineState || engineState.status === ENGINE_NONE) return null
  const mvp2 = engineState.mvp2 || {}
  const evalRange = mvp2.evalRange || {}
  const engine = mvp2.engine || {}
  const selected = ctx.evaluationRange || null
  const stats = selected ? timingStats(ctx) : null
  const timing = selected ? timingConsistency(ctx) : null
  const startSec = selected ? numberOrNull(selected.startSec) : null
  const endSec = selected ? numberOrNull(selected.endSec) : null
  const durationSec = selected ? numberOrNull(selected.durationSec) : null
  const engineValues = stats?.ratioCalculation === ENGINE_RATIO_CALCULATION
  const heartRateMethod = engineValues ? ENGINE_HEART_RATE_METHOD : CYCLE_HEART_RATE_METHOD
  return {
    schema: EVALUATION_RANGE_PROVENANCE_SCHEMA,
    status: engineState.status,
    engineStatus: mvp2.status ?? null,
    outcome: evalRange.outcome ?? null,
    selectionMode: evalRange.selectionMode ?? null,
    startSec,
    endSec,
    durationSec: durationSec ?? (startSec !== null && endSec !== null ? endSec - startSec : null),
    cycleCount: selected ? numberOrNull(selected.timing?.cycleCount) : null,
    engine: {
      package: engine.package ?? null,
      packageVersion: engine.packageVersion ?? null,
      settingsVersion: engine.settingsVersion ?? null,
    },
    calculationMethod: stats?.ratioCalculation || null,
    heartRateMethod: stats ? heartRateMethod : null,
    s1IntervalCv: stats ? stats.s1IntervalCv : null,
    s1IntervalCvDescription: S1_INTERVAL_CV_DESCRIPTION,
    timingVariabilityMethod: timing?.spreadMethod || null,
  }
}

function cycleTimingStats(ctx) {
  const D = ctx.payload
  const quality = analysisQuality(ctx)
  const cycles = quality.summaryAllowed ? quality.summaryCycles : quality.usableCycles
  const s1s = clinicalPeaks(D).filter((p) => p.l === "S1")
  if (!cycles.length)
    return {
      cycles,
      hr: "-",
      n: s1s.length,
      cycle: "-",
      sys: "-",
      dia: "-",
      ratio: "-",
      frac: "-",
      cv: "-",
      outliers: "-",
    }
  const sys = cycles.map((c) => c.sys),
    dia = cycles.map((c) => c.dia),
    cyc = cycles.map((c) => c.cycle),
    ratios = cycles.map((c) => c.ratio)
  const meanSys = mean(sys)
  const meanDia = mean(dia)
  // Canonical exam-viewer mode (hard-enabled): S/D ratio is the ratio of mean
  // accepted cycle durations; legacy mean-of-per-cycle-ratios kept for audit only.
  const canonicalTiming = canonicalTimingDurationSummary(cycles)
  const canonicalSdRatio = canonicalTiming ? canonicalTiming.sdRatioExact : null
  const legacyMeanCycleRatio = mean(ratios)
  const cycleMed = median(cyc)
  const cv = sd(cyc) / (mean(cyc) || 1)
  const outliers = cycles.filter(
    (c) => Math.abs(c.cycle - cycleMed) > 0.18 * cycleMed || c.ratio > 0.9 || c.ratio < 0.35
  ).length
  const examTiming = ctx.examTimingConsistency
  const examTimingItem =
    examTiming && Array.isArray(examTiming.recordings)
      ? examTiming.recordings.find((item) => item.recordingId === ctx.currentRecordingId)
      : null
  const calculatedHr = Math.round(60 / mean(cyc))
  const correctedHr =
    examTimingItem && examTimingItem.autoCorrected && Number.isFinite(Number(examTimingItem.correctedBpm))
      ? Number(examTimingItem.correctedBpm)
      : calculatedHr
  return {
    cycles,
    hr: correctedHr,
    rawHr: calculatedHr,
    examConsensusCorrection: examTimingItem && examTimingItem.autoCorrected ? examTimingItem : null,
    n: cycles.length,
    cycle: (mean(cyc) * 1000).toFixed(0),
    sys: (meanSys * 1000).toFixed(0),
    dia: (meanDia * 1000).toFixed(0),
    sysExactMs: canonicalTiming ? canonicalTiming.meanSystolicMsExact : meanSys * 1000,
    diaExactMs: canonicalTiming ? canonicalTiming.meanDiastolicMsExact : meanDia * 1000,
    ratio: (canonicalSdRatio !== null ? canonicalSdRatio : legacyMeanCycleRatio).toFixed(6),
    ratioCalculation: "ratio_of_mean_accepted_cycle_durations",
    legacyMeanCycleRatio,
    canonicalSdRatio,
    selectedTimingCycleIds: canonicalTiming ? canonicalTiming.selectedTimingCycleIds : cycleAuditIds(cycles),
    frac: (mean(sys.map((v, i) => v / cyc[i])) * 100).toFixed(0),
    cv: cv.toFixed(3),
    outliers,
  }
}

// Cross-position exam-consensus cues (core-viewer.js 4063-4072, verbatim modulo
// the window-global reads: __medaicaExamTimingConsistency / __medaicaCurrentRecordingId
// became ctx.examTimingConsistency / ctx.currentRecordingId). Vendored here
// because the Confidence (murmur-evidence) card's Timing row consumes these
// instead of the exam overlay's DOM text-scraping pass.
// Upstream v0.9 returns no cross-position cue under the `pcg-exam-v0900` body
// class; that rule takes effect on its page only once the timing engine has
// re-rendered the results, i.e. for a guide with a ready Evaluation Range.
export function crossPositionTimingReview(ctx) {
  if (ctx.evaluationRange) return null
  const review = ctx.examTimingConsistency
  if (!review || !Array.isArray(review.recordings)) return null
  return (
    review.recordings.find(
      (item) => item.recordingId === ctx.currentRecordingId && !item.autoCorrected && item.status !== "consistent"
    ) || null
  )
}

export function crossPositionTimingCorrection(ctx) {
  if (ctx.evaluationRange) return null
  const review = ctx.examTimingConsistency
  if (!review || !Array.isArray(review.recordings)) return null
  return review.recordings.find((item) => item.recordingId === ctx.currentRecordingId && item.autoCorrected) || null
}

export function timingConsistency(ctx) {
  const D = ctx.payload
  const quality = analysisQuality(ctx)
  const cycles = quality.summaryAllowed ? quality.summaryCycles : []
  if (cycles.length < MIN_USABLE_CYCLES) {
    const codes = ["low_cycle_count"]
    return {
      label: "Limited",
      index: "-",
      detail: cycles.length
        ? `Unable to assess timing variability: needs at least ${MIN_USABLE_CYCLES} selected usable S1-to-S1 cycles; found ${cycles.length}.`
        : quality.reason || "Not enough usable S1-to-S1 cycles",
      confidenceFlag: "Insufficient selected cycles",
      timingConfidence: "N/A",
      metricUsed: "N/A",
      confidenceRationaleCodes: codes,
      confidenceRationaleText: confidenceRationaleText(codes),
      timingMethodFlags: [],
      usableCycleCount: cycles.length,
    }
  }
  const cyc = cycles.map((c) => c.cycle)
  const cycleMed = median(cyc)
  const cv = sd(cyc) / (mean(cyc) || 1)
  let metricUsed = "P90-P10"
  let spreadMethod = "P90-P10 central spread"
  let pLow = null
  let pHigh = null
  let robustSpreadPct = null
  let timingSwingPct = null
  let robustSpreadMs = null
  let timingSwingMs = null
  const timingMethodFlags = []
  const confidenceRationaleCodes = []
  if (cycles.length >= 25) {
    pLow = percentile(cyc, 0.1)
    pHigh = percentile(cyc, 0.9)
    robustSpreadPct = cycleMed > 0 ? (100 * (pHigh - pLow)) / cycleMed : null
    timingSwingPct = Number.isFinite(robustSpreadPct) ? robustSpreadPct / 2 : null
    robustSpreadMs = Number.isFinite(pHigh) && Number.isFinite(pLow) ? (pHigh - pLow) * 1000 : null
    timingSwingMs = Number.isFinite(robustSpreadMs) ? robustSpreadMs / 2 : null
  } else if (cycles.length >= 10) {
    metricUsed = "P80-P20"
    spreadMethod = "P80-P20 central spread"
    timingMethodFlags.push("metric_p80p20")
    pLow = percentile(cyc, 0.2)
    pHigh = percentile(cyc, 0.8)
    robustSpreadPct = cycleMed > 0 ? (100 * (pHigh - pLow)) / cycleMed : null
    timingSwingPct = Number.isFinite(robustSpreadPct) ? robustSpreadPct / 2 : null
    robustSpreadMs = Number.isFinite(pHigh) && Number.isFinite(pLow) ? (pHigh - pLow) * 1000 : null
    timingSwingMs = Number.isFinite(robustSpreadMs) ? robustSpreadMs / 2 : null
  } else {
    metricUsed = "CV"
    spreadMethod = "CV timing spread"
    timingMethodFlags.push("metric_cv")
    confidenceRationaleCodes.push("low_cycle_count")
    timingSwingPct = Number.isFinite(cv) ? cv * 100 : null
    robustSpreadPct = Number.isFinite(timingSwingPct) ? timingSwingPct * 2 : null
    timingSwingMs =
      Number.isFinite(timingSwingPct) && Number.isFinite(cycleMed) ? (timingSwingPct / 100) * cycleMed * 1000 : null
    robustSpreadMs = Number.isFinite(timingSwingMs) ? timingSwingMs * 2 : null
  }
  const medianCycleMs = Number.isFinite(cycleMed) ? cycleMed * 1000 : null
  const hr = cycleMed > 0 ? 60 / cycleMed : null
  const thresholds = bpmAdjustedTimingThresholds(hr)
  if (thresholds.rateAdjusted) timingMethodFlags.push("rate_adjustment_applied")
  const successiveDifferencePct = normalizedSuccessiveDifferencePct(cyc)
  const lag1Autocorrelation = lagAutocorrelation(cyc, 1)
  const metricBoundaryMarkers = []
  const seenMarkers = new Set()
  cycles.forEach((c) =>
    [c.s1, c.nextS1].forEach((p) => {
      const key = `${p.l}:${p.t.toFixed(4)}`
      if (!seenMarkers.has(key)) {
        seenMarkers.add(key)
        metricBoundaryMarkers.push(p)
      }
    })
  )
  const weakBoundaryCount = metricBoundaryMarkers.filter(lowConfidenceMarker).length
  const weakBoundaryPct = metricBoundaryMarkers.length ? (100 * weakBoundaryCount) / metricBoundaryMarkers.length : 0
  const inferredBoundaryCount = metricBoundaryMarkers.filter((p) => p.source === "inferred").length
  const inferredMarkerPct = metricBoundaryMarkers.length
    ? (100 * inferredBoundaryCount) / metricBoundaryMarkers.length
    : 0
  const gateMetrics = rejectGateMetrics(D)
  const guideQuality = String((D.metrics && D.metrics.quality) || "").toUpperCase()
  const highConfidenceMarkerPct = Number.isFinite(Number(D.metrics && D.metrics.pct_high_conf))
    ? Number(D.metrics.pct_high_conf) * 100
    : metricBoundaryMarkers.length
    ? (100 * metricBoundaryMarkers.filter((p) => !lowConfidenceMarker(p)).length) / metricBoundaryMarkers.length
    : null
  const orientationScore = Number.isFinite(Number(D.score))
    ? Number(D.score)
    : Number.isFinite(Number(gateMetrics.orientation_score))
    ? Number(gateMetrics.orientation_score)
    : null
  const noiseFloorFlag = false
  const close = cycles.filter((c) => Math.abs(c.cycle - cycleMed) <= 0.1 * cycleMed).length
  const closePct = (100 * close) / cycles.length
  const outlierCount = cycles.filter((c) => Math.abs(c.cycle - cycleMed) > 0.18 * cycleMed).length
  const base = 0.7 * closePct + 30 * (1 - Math.min(cv / 0.12, 1))
  const penalty = Math.min(20, outlierCount * 4)
  const index = Math.max(0, Math.min(100, Math.round(base - penalty)))
  if (guideQuality === "LOW") confidenceRationaleCodes.push("guide_quality_low")
  else if (guideQuality === "MODERATE") confidenceRationaleCodes.push("guide_quality_moderate")
  if (Number.isFinite(highConfidenceMarkerPct) && highConfidenceMarkerPct < 33.333)
    confidenceRationaleCodes.push("low_confidence_marker_share")
  if (Number.isFinite(orientationScore) && orientationScore < 0.45)
    confidenceRationaleCodes.push("orientation_score_weak")
  if (inferredMarkerPct > 30) confidenceRationaleCodes.push("inferred_markers_high")
  if (noiseFloorFlag) confidenceRationaleCodes.push("noise_floor_flag")
  if (cycles.length >= 10 && cycles.length <= 14) confidenceRationaleCodes.push("periodic_assessment_limited")
  const hardBlock = inferredMarkerPct > 50 || weakBoundaryPct > 50
  if (hardBlock) {
    const hardCodes = [
      ...new Set([
        ...confidenceRationaleCodes,
        inferredMarkerPct > 50 ? "inferred_markers_high" : "low_confidence_marker_share",
      ]),
    ]
    return {
      label: "Limited",
      index,
      cv,
      closePct,
      robustSpreadPct,
      timingSwingPct,
      robustSpreadMs,
      timingSwingMs,
      medianCycleMs,
      successiveDifferencePct,
      lag1Autocorrelation,
      expectedNormalVariabilityPct: thresholds.normalMaxPct,
      expectedNormalSwingPct: thresholds.normalMaxPct / 2,
      irregularVariabilityPct: thresholds.irregularMinPct,
      irregularSwingPct: thresholds.irregularMinPct / 2,
      bpmForThreshold: hr,
      outlierCount,
      spreadMethod,
      metricUsed,
      weakBoundaryPct,
      inferredMarkerPct,
      highConfidenceMarkerPct,
      guideQuality,
      orientationScore,
      noiseFloorFlag,
      confidenceFlag: "Unable to assess: low marker confidence",
      timingConfidence: "N/A",
      confidenceRationaleCodes: hardCodes,
      confidenceRationaleText: confidenceRationaleText(hardCodes),
      timingMethodFlags,
      usableCycleCount: cycles.length,
      detail: `Unable to assess timing variability: ${fmtNumber(
        Math.max(weakBoundaryPct, inferredMarkerPct),
        0
      )}% of S1 cycle-boundary markers were inferred or low confidence.`,
    }
  }
  let label = "Moderate PCG timing variability"
  const lowMeasuredSpread = Number.isFinite(timingSwingPct) && timingSwingPct < 2
  if (Number.isFinite(timingSwingPct) && timingSwingPct <= thresholds.normalSwingPct)
    label = "Expected PCG timing variability"
  else if (
    Number.isFinite(timingSwingPct) &&
    timingSwingPct > thresholds.elevatedSwingPct &&
    quality.markerConfidence >= 0.75
  )
    label = "High PCG timing variability"
  const periodic =
    cycles.length >= 10 &&
    Number.isFinite(lag1Autocorrelation) &&
    lag1Autocorrelation <= -0.5 &&
    label !== "Expected PCG timing variability"
  if (periodic) label = "Periodic PCG timing pattern"
  if (cycles.length < 10 && label !== "Expected PCG timing variability")
    timingMethodFlags.push("periodic_detection_suppressed")
  if (cycles.length >= 10 && cycles.length < 25) timingMethodFlags.push("periodic_detection_screened")
  const flags = []
  if (weakBoundaryPct >= 20) flags.push("Low marker confidence")
  if (lowMeasuredSpread) flags.push("Very low measured spread; interpret as noise-floor/supporting evidence only")
  if (Number.isFinite(hr) && hr < 60 && label !== "Expected PCG timing variability")
    flags.push("Respiratory variation possible at lower heart rate")
  let timingConfidence =
    quality.markerConfidence >= 0.75 ? "HIGH" : quality.markerConfidence >= 0.45 ? "MODERATE" : "LOW"
  if (guideQuality === "MODERATE" && timingConfidence === "HIGH") timingConfidence = "MODERATE"
  if (guideQuality === "LOW") timingConfidence = "LOW"
  if (Number.isFinite(highConfidenceMarkerPct) && highConfidenceMarkerPct < 33.333) timingConfidence = "LOW"
  if (Number.isFinite(orientationScore) && orientationScore < 0.45) timingConfidence = "LOW"
  if (cycles.length >= 5 && cycles.length <= 9) {
    const shortClean = guideQuality === "HIGH" && inferredMarkerPct < 20 && !noiseFloorFlag
    timingConfidence = shortClean ? "MODERATE" : "LOW"
  }
  if (inferredMarkerPct > 30 && timingConfidence === "HIGH") timingConfidence = "MODERATE"
  const confidenceText = confidenceRationaleText(confidenceRationaleCodes)
  return {
    label,
    index,
    cv,
    closePct,
    robustSpreadPct,
    timingSwingPct,
    robustSpreadMs,
    timingSwingMs,
    medianCycleMs,
    successiveDifferencePct,
    lag1Autocorrelation,
    expectedNormalVariabilityPct: thresholds.normalMaxPct,
    expectedNormalSwingPct: thresholds.normalMaxPct / 2,
    irregularVariabilityPct: thresholds.irregularMinPct,
    irregularSwingPct: thresholds.irregularMinPct / 2,
    bpmForThreshold: hr,
    outlierCount,
    spreadMethod,
    metricUsed,
    weakBoundaryPct,
    inferredMarkerPct,
    highConfidenceMarkerPct,
    guideQuality,
    orientationScore,
    noiseFloorFlag,
    lowMeasuredSpread,
    confidenceFlag: flags.join("; "),
    timingConfidence,
    confidenceRationaleCodes: [...new Set(confidenceRationaleCodes)],
    confidenceRationaleText: confidenceText,
    timingMethodFlags: [...new Set(timingMethodFlags)],
    usableCycleCount: cycles.length,
    periodicPattern: periodic,
    detail: `${cycles.length} S1-to-S1 cycles · approx timing variation ±${fmtNumber(
      timingSwingPct,
      1
    )}% (${metricUsed}) · Normal up to about ±${fmtNumber(
      thresholds.normalSwingPct,
      0
    )}% · Moderate through about ±18% · successive-difference ${fmtNumber(
      successiveDifferencePct,
      1
    )}% · lag-1 autocorrelation ${fmtNumber(lag1Autocorrelation, 2)}${flags.length ? " · " + flags.join("; ") : ""}${
      confidenceText ? " · " + confidenceText : ""
    }`,
  }
}

export function rhythmPatternFromTiming(ctx, timing) {
  const quality = analysisQuality(ctx)
  const cycles = quality.summaryAllowed ? quality.summaryCycles : []
  if (!cycles.length || !timing || !Number.isFinite(Number(timing.cv))) {
    return {
      label: "N/A",
      className: "unavailable",
      hover: quality.reason || "Rhythm pattern unavailable because there are not enough usable PCG S1-to-S1 cycles.",
    }
  }
  const cyc = cycles.map((c) => c.cycle)
  const cycleMed = median(cyc)
  const swingPct = Number(timing.timingSwingPct)
  const swingMs = Number(timing.timingSwingMs)
  const medianCycleMs = Number(timing.medianCycleMs)
  const expectedPct = Number(timing.expectedNormalSwingPct)
  const elevatedPct = Number(timing.irregularSwingPct)
  const markerConfidence = Number(quality.markerConfidence || 0)
  const patterned = alternatingCyclePattern(cycles, cycleMed)
  const caveat = `${
    timing.label === "Limited" ? "Lower-confidence provisional timing evidence for clinician review. " : ""
  }PCG timing estimate; confirm with clinical review when needed.`
  const msText =
    Number.isFinite(swingMs) && Number.isFinite(medianCycleMs)
      ? `, about ±${fmtNumber(swingMs, 0)} ms around a ${fmtNumber(medianCycleMs, 0)} ms median cycle`
      : ""
  const base = `Approximate S1-to-S1 timing variation is ±${fmtNumber(swingPct, 1)}%${msText}. ${caveat}`
  if (Number.isFinite(swingPct) && Number.isFinite(expectedPct) && swingPct <= expectedPct) {
    return {
      label: "Normal",
      className: "",
      hover: `Normal timing variability. ${base}`,
    }
  }
  if (timing.label === "Periodic PCG timing pattern" || patterned) {
    return {
      label: "Periodic",
      className: "",
      hover: `Repeating timing pattern detected. ${base}`,
    }
  }
  if (Number.isFinite(swingPct) && Number.isFinite(elevatedPct) && swingPct > elevatedPct && markerConfidence >= 0.75) {
    return {
      label: "Elevated",
      className: "",
      hover: `Elevated timing variability. ${base}`,
    }
  }
  return {
    label: "Moderate",
    className: "",
    hover: `Moderate timing variability. ${base}`,
  }
}
